import "./videoPlayer.scss";
import Player from '@vimeo/player';
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

type VideoPlayerHook = (data?: any) => void;

type VideoPlayerOptions = {
  mediaId?: string;
  autoplay?: boolean;
  playerControls?: boolean;
  noSeek?: boolean;
  onReady?: VideoPlayerHook;
  onPlay?: VideoPlayerHook;
  onPause?: VideoPlayerHook;
  onEnded?: VideoPlayerHook;
  onError?: (error: Error) => void;
  onLoaded?: VideoPlayerHook;
};

export class VideoPlayer {
  private readonly id: string;
  private static registry = new ComponentRegistry<VideoPlayer>('video-player');
  private readonly options: Required<Omit<VideoPlayerOptions, 'onReady' | 'onPlay' | 'onPause' | 'onEnded' | 'onError' | 'onLoaded'>> & 
    Pick<VideoPlayerOptions, 'onReady' | 'onPlay' | 'onPause' | 'onEnded' | 'onError' | 'onLoaded'>;
  private playerContainer: HTMLElement;
  private videoPlayer: Player | null = null;

  private constructor(
    playerContainer: HTMLElement,
    options: VideoPlayerOptions
  ) {
    this.playerContainer = playerContainer;
    this.id = VideoPlayer.registry.register(this.playerContainer, this);
    this.options = {
      mediaId: options.mediaId ?? '727395867',
      autoplay: options.autoplay ?? false,
      playerControls: options.playerControls ?? true,
      noSeek: options.noSeek ?? false,
      onReady: options.onReady,
      onPlay: options.onPlay,
      onPause: options.onPause,
      onEnded: options.onEnded,
      onError: options.onError,
      onLoaded: options.onLoaded
    };
    
    this.initPlayer();
  }

  private initPlayer = (): void => {
    try {
      const player = new Player(this.playerContainer, {
        id: Number(this.options.mediaId),
        autoplay: this.options.autoplay,
        controls: this.options.playerControls,
        keyboard: !this.options.noSeek
      });

      this.videoPlayer = player;
      this.bindPlayerEvents();
      
      if (this.options.onReady) {
        this.options.onReady({ playerId: this.id });
      }
    } catch (error) {
      if (this.options.onError) {
        this.options.onError(error instanceof Error ? error : new Error(String(error)));
      }
    }
  };

  private setAspectRatio = async (): Promise<void> => {
    if (!this.videoPlayer) return;

    try {
      const [width, height] = await Promise.all([
        this.videoPlayer.getVideoWidth(),
        this.videoPlayer.getVideoHeight()
      ]);

      const orientation = height > width ? 'portrait' : 'landscape';
      this.playerContainer.dataset.orientation = orientation;
      this.playerContainer.style.aspectRatio = `${width} / ${height}`;
    } catch {
      // Fall back to landscape if we can't determine dimensions
      this.playerContainer.dataset.orientation = 'landscape';
      this.playerContainer.style.aspectRatio = '16 / 9';
    }
  };

  private bindPlayerEvents = (): void => {
    if (!this.videoPlayer) return;

    this.videoPlayer.on('loaded', async () => {
      await this.setAspectRatio();
      if (this.options.onLoaded) {
        this.options.onLoaded({ playerId: this.id });
      }
    });

    this.videoPlayer.on('play', (data) => {
      if (this.options.onPlay) {
        this.options.onPlay(data);
      }
    });

    this.videoPlayer.on('pause', (data) => {
      if (this.options.onPause) {
        this.options.onPause(data);
      }
    });

    this.videoPlayer.on('ended', (data) => {
      if (this.options.onEnded) {
        this.options.onEnded(data);
      }
    });

    this.videoPlayer.on('error', (error) => {
      if (this.options.onError) {
        this.options.onError(new Error(error.message));
      }
    });
  };

  public loadVideo = async (mediaId: string): Promise<boolean> => {
    if (!this.videoPlayer) {
      if (this.options.onError) {
        this.options.onError(new Error('Video player not initialized'));
      }
      return false;
    }

    try {
      await this.videoPlayer.loadVideo(Number(mediaId));
      return true;
    } catch (error) {
      if (this.options.onError) {
        this.options.onError(error instanceof Error ? error : new Error(String(error)));
      }
      return false;
    }
  };

  public play = async (): Promise<void> => {
    if (this.videoPlayer) {
      await this.videoPlayer.play();
    }
  };

  public pause = async (): Promise<void> => {
    if (this.videoPlayer) {
      await this.videoPlayer.pause();
    }
  };

  public static create = (
    target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
    options: VideoPlayerOptions = {}
  ): VideoPlayer[] | null => {
    const triggers = ComponentRegistry.processTargets<VideoPlayer, HTMLElement>(
      VideoPlayer.registry,
      target,
      [HTMLElement]
    ) as HTMLElement[];

    if (!triggers.length) return null;
    return triggers.map(trigger => new VideoPlayer(trigger, options));
  };

public static getThumbnail = async (videoId: string, width: number = 1920): Promise<string | null> => {
  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${encodeURIComponent(videoId)}&width=${width}`
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.thumbnail_url ?? null;
  } catch {
    return null;
  }
};

  public static getPlayer(id: string): VideoPlayer | null {
    return VideoPlayer.registry.get(id) ?? null;
  }

  public static getVimeoPlayer(id: string): Player | null {
    const videoPlayer = VideoPlayer.registry.get(id);
    return videoPlayer?.videoPlayer ?? null;
  }

  public getId = (): string => this.id;

  public destroy = (): void => {
    if (this.videoPlayer) {
      this.videoPlayer.destroy();
      this.videoPlayer = null;
    }
    VideoPlayer.registry.unregister(this.id);
  };
}